/** tm/legal/LegalNoticeDetail.tsx — 입법예고 상세 (GET /notices/:id): 법령종류·소관부처·예고기간·의견제출 D-n · 개정이유·주요내용 · 진행 단계(국무회의·공포까지) · 원문·의견 제출 */
import { useCallback, useEffect, useState } from 'react';
import { C, T } from '../components/ui';
import { getNotice, setNoticeRead, setNoticeStar } from './legalApi';
import { btn, shortDate, safeHttp, errMsg, lawKindLabel, noticeDday, AMBER, MUTED } from './legalTheme';
import { DetailHead, MetaGrid, Box, DetailEmpty, DetailSkeleton, DETAIL_MAX, actsRow } from './LegalDetailParts';
import LegalStageTimeline from './LegalStageTimeline';
import type { NoticeDetail, Selection } from './legalTypes';

interface Props {
  id: number; mobile: boolean;
  onBack: () => void; onToast: (m: string, k?: 'ok' | 'error') => void;
  onPatch: (p: { starred?: boolean; unread?: boolean }) => void;
  onOpen?: (s: NonNullable<Selection>) => void;
}

export default function LegalNoticeDetail({ id, mobile, onBack, onToast, onPatch, onOpen }: Props) {
  const [n, setN] = useState<NoticeDetail | null>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setN(null); setErr('');
    try { setN(await getNotice(id)); } catch (e) { setErr(errMsg(e, '입법예고를 불러오지 못했습니다')); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <>{mobile && <button type="button" className="lw-f" onClick={onBack} style={btn()}>← 목록</button>}<DetailEmpty error={err} onRetry={load} /></>;
  if (!n) return <DetailSkeleton />;

  const star = async () => {
    const next = !n.starred;
    setN({ ...n, starred: next }); onPatch({ starred: next });
    try { await setNoticeStar(n.id, next); onToast(next ? '별표했습니다' : '별표를 해제했습니다'); }
    catch (e) { setN({ ...n, starred: !next }); onPatch({ starred: !next }); onToast(errMsg(e, '별표하지 못했습니다'), 'error'); }
  };
  const unread = async () => {
    onPatch({ unread: true });
    try { await setNoticeRead(n.id, false); onToast('안 읽음으로 바꿨습니다'); }
    catch (e) { onPatch({ unread: false }); onToast(errMsg(e, '바꾸지 못했습니다'), 'error'); }
  };
  const d = noticeDday(n.days_left, n.end_date);
  const url = safeHttp(n.detail_url);
  const dday = d && <b style={{ marginLeft: 8, color: d.open ? (d.urgent ? C.red : C.accent) : MUTED }}>{d.text}</b>;

  return (
    <div>
      <DetailHead group="입법예고" label="입법예고" topics={n.topics || []} title={n.title} mobile={mobile} onBack={onBack} stage="notice" />
      <MetaGrid mobile={mobile} rows={[
        ['법령 종류', lawKindLabel(n.law_kind) || '-'],
        ['소관 부처', n.ministry || '-'],
        ['예고 기간', <>{shortDate(n.start_date)} ~ {shortDate(n.end_date)}{dday}</>],
        ...(n.law_refs && n.law_refs.length ? [['관련 조문', n.law_refs.join(' · ')] as [string, string]] : []),
      ]} />
      {n.summary
        ? <Box title="개정이유 · 주요내용">{n.summary}</Box>
        : <Box kind="q" title="개정이유 · 주요내용">요약을 아직 받지 못했습니다. 원문에서 확인해 주세요.</Box>}
      <LegalStageTimeline kind="notice" id={n.id} onOpen={onOpen} />
      <p style={{ fontSize: T.sm, color: MUTED, maxWidth: DETAIL_MAX, lineHeight: 1.6, margin: '0 0 12px' }}>
        입법예고는 정부가 법령을 바꾸기 전에 내용을 미리 알리고 의견을 받는 절차입니다. 예고 기간 안에 국민참여입법센터에서 의견을 낼 수 있습니다.
      </p>
      <div style={actsRow}>
        {url && <a className="lw-f" href={url} target="_blank" rel="noopener noreferrer" style={btn('pri')}>{d?.open === false ? '입법예고 원문 보기' : '입법예고 원문·의견 제출'}</a>}
        <button type="button" className="lw-f" onClick={star} aria-pressed={n.starred} style={{ ...btn(), color: n.starred ? AMBER : C.sub }}>{n.starred ? '★ 별표 해제' : '☆ 별표'}</button>
        <button type="button" className="lw-f" onClick={unread} style={btn()}>안 읽음으로</button>
      </div>
    </div>
  );
}
